// Figma: "Loyalty Cloud" file, node 18093:65 ("Profile / Default") — two form cards:
// Name (first/last name, Save) and Email (read-only verified email, Update triggers a
// re-verification flow, not implemented server-side yet). Wired to the same
// updateTeamMemberPersonalInfo mutation used elsewhere for personal-info edits — its
// matchFulfilled in session.js already updates firstName/lastName/email on success.
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, Button, Dialog, Stack, TextField, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import {tokens} from '../../themes/ThemeCustomization';
import Badge from '../Dashboard/components/Badge';
import AuthToast from '../authentication/components/AuthToast';
import SpinnerIcon from '../authentication/components/SpinnerIcon';
import buttonSpinnerIcon from '../../assets/auth/button-spinner-icon.svg';
import validateEmail from '../../commons/utils/emailValidation';
import {getEmail, getFirstName, getLastName, getLcTeamMemberId} from '../../app/store/reducers/session';
import {useUpdateTeamMemberPersonalInfoMutation} from '../../features/loyaltyCloudApi/apiSlice';
import mailSuccessIcon from '../../assets/icons/settings/mail-success-icon.svg';

const FormCard = ({heading, description, children, footerNote, actionLabel, onAction, actionDisabled, actionLoading}) => (
    <Box sx={{backgroundColor: tokens.color.white, border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, width: '100%'}}>
        <Box sx={{p: '20px'}}>
            <Typography sx={{fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: tokens.color.neutral950}}>{heading}</Typography>
            <Typography variant="body2" sx={{color: tokens.color.neutral600, pt: 1, pb: '20px'}}>
                {description}
            </Typography>
            {children}
        </Box>
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
                minHeight: '56px',
                pl: '20px',
                pr: 1.5,
                py: 2,
                backgroundColor: tokens.color.neutral50,
                borderTop: `1px solid ${tokens.color.neutral200}`,
                borderRadius: `0 0 ${tokens.radius.card} ${tokens.radius.card}`
            }}
        >
            <Typography variant="body2" sx={{color: tokens.color.neutral600}}>
                {footerNote}
            </Typography>
            <LoadingButton
                variant="contained"
                disabled={actionDisabled}
                loading={actionLoading}
                loadingPosition="start"
                onClick={onAction}
                startIcon={<Box sx={{width: 0}} />}
                sx={{
                    backgroundColor: tokens.color.sky600,
                    height: 32,
                    px: 1.5,
                    '&.Mui-disabled': actionLoading ? {backgroundColor: tokens.color.sky600, color: tokens.color.white} : undefined
                }}
                loadingIndicator={<SpinnerIcon src={buttonSpinnerIcon} />}
            >
                {actionLabel}
            </LoadingButton>
        </Stack>
    </Box>
);

// Figma: "Loyalty Cloud" file, node 18093:2869 ("Profile / Change Email Modal — Empty").
const ChangeEmailModal = ({open, currentEmail, onClose, onSave, isLoading}) => {
    const [newEmail, setNewEmail] = useState('');

    useEffect(() => {
        if (open) {
            setNewEmail('');
        }
    }, [open]);

    const canSave = newEmail.trim().length > 0 && validateEmail(newEmail.trim()) && newEmail.trim() !== currentEmail;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '400px',
                    borderRadius: tokens.radius.card,
                    border: `1px solid ${tokens.color.neutral200}`,
                    boxShadow: tokens.shadow.card,
                    p: '20px'
                }
            }}
        >
            <Typography sx={{fontSize: '16px', fontWeight: 600, lineHeight: '24px', color: tokens.color.neutral950}}>Change Email</Typography>
            <Typography variant="body2" sx={{color: tokens.color.neutral600, pt: 1, pb: '20px'}}>
                Enter a new email address for your account. Once verified, it can be used to log in.
            </Typography>

            <Typography variant="body2" sx={{color: tokens.color.neutral950, pb: 1}}>
                Email
            </Typography>
            <TextField
                fullWidth
                size="small"
                placeholder="your@email.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                sx={{mb: '20px'}}
            />

            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Button variant="outlined" onClick={onClose} sx={{height: 32, px: 1.5, color: tokens.color.neutral950, borderColor: tokens.color.neutral200}}>
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    disabled={!canSave || isLoading}
                    loading={isLoading}
                    loadingPosition="start"
                    onClick={() => onSave(newEmail.trim())}
                    startIcon={<Box sx={{width: 0}} />}
                    sx={{
                        backgroundColor: tokens.color.sky600,
                        height: 32,
                        px: 1.5,
                        '&.Mui-disabled': isLoading ? {backgroundColor: tokens.color.sky600, color: tokens.color.white} : undefined
                    }}
                    loadingIndicator={<SpinnerIcon src={buttonSpinnerIcon} />}
                >
                    Save
                </LoadingButton>
            </Stack>
        </Dialog>
    );
};

// Figma: "Loyalty Cloud" file, node 18093:3379 ("Profile / Pending Email Verification").
const PendingEmailBanner = ({pendingEmail, onCancel}) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
            mt: 1.5,
            height: '48px',
            px: 1.5,
            gap: 1,
            backgroundColor: tokens.color.success50,
            border: `1px solid ${tokens.color.success200}`,
            borderRadius: tokens.radius.control
        }}
    >
        <Stack direction="row" alignItems="center" spacing={1}>
            <Box component="img" src={mailSuccessIcon} alt="" sx={{width: 16, height: 16}} />
            <Typography variant="body2" sx={{color: tokens.color.success700}}>
                A verification link has been sent to {pendingEmail}. Please check your inbox to confirm the change.
            </Typography>
        </Stack>
        <Button
            variant="outlined"
            onClick={onCancel}
            sx={{height: 32, px: 1.5, backgroundColor: tokens.color.white, color: tokens.color.neutral950, borderColor: tokens.color.neutral200}}
        >
            Cancel
        </Button>
    </Stack>
);

const ProfileSettingsPage = () => {
    const sessionFirstName = useSelector(getFirstName);
    const sessionLastName = useSelector(getLastName);
    const email = useSelector(getEmail);
    const teamMemberId = useSelector(getLcTeamMemberId);

    const [firstName, setFirstName] = useState(sessionFirstName || '');
    const [lastName, setLastName] = useState(sessionLastName || '');
    const [toast, setToast] = useState(null);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [pendingEmail, setPendingEmail] = useState(null);
    const [updatePersonalInfo, {isLoading}] = useUpdateTeamMemberPersonalInfoMutation();
    const [updateEmail, {isLoading: isEmailLoading}] = useUpdateTeamMemberPersonalInfoMutation();

    useEffect(() => {
        setFirstName(sessionFirstName || '');
        setLastName(sessionLastName || '');
    }, [sessionFirstName, sessionLastName]);

    const nameUnchanged = firstName === (sessionFirstName || '') && lastName === (sessionLastName || '');

    const handleSaveName = async () => {
        const data = new FormData();
        data.append('first_name', firstName);
        data.append('last_name', lastName);
        try {
            await updatePersonalInfo({data, id: teamMemberId}).unwrap();
            setToast({title: 'Your name has been updated.', variant: 'success'});
        } catch {
            setToast({title: 'Unable to save', description: "We couldn't update your name. Please try again.", variant: 'error'});
        }
    };

    const handleSaveEmail = async (newEmail) => {
        const data = new FormData();
        data.append('email', newEmail);
        try {
            await updateEmail({data, id: teamMemberId}).unwrap();
            setEmailModalOpen(false);
            setPendingEmail(newEmail);
            setToast({title: `We've sent ${newEmail} an email to verify your new email address`, variant: 'success'});
        } catch {
            setToast({title: 'Unable to save', description: "We couldn't update your email. Please try again.", variant: 'error'});
        }
    };

    const handleCancelPendingEmail = () => {
        setPendingEmail(null);
    };

    return (
        <Stack sx={{gap: 4, pb: 6, pt: 3}}>
            <FormCard
                heading="Name"
                description="Update your first and last name as they appear on your profile."
                footerNote="Please use your real name so teammates can recognize you."
                actionLabel="Save"
                actionDisabled={nameUnchanged || isLoading}
                actionLoading={isLoading}
                onAction={handleSaveName}
            >
                <Stack direction="row" spacing={2}>
                    <Box sx={{flex: 1}}>
                        <Typography variant="body2" sx={{color: tokens.color.neutral950, pb: 1}}>
                            First Name
                        </Typography>
                        <TextField fullWidth size="small" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </Box>
                    <Box sx={{flex: 1}}>
                        <Typography variant="body2" sx={{color: tokens.color.neutral950, pb: 1}}>
                            Last Name
                        </Typography>
                        <TextField fullWidth size="small" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </Box>
                </Stack>
            </FormCard>

            <FormCard
                heading="Email"
                description="This is the email address used to log in to your account."
                footerNote="We'll send a confirmation link to verify your new email address."
                actionLabel="Update"
                onAction={() => setEmailModalOpen(true)}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    onClick={() => setEmailModalOpen(true)}
                    sx={{
                        height: '40px',
                        border: `1px solid ${tokens.color.neutral200}`,
                        borderRadius: tokens.radius.control,
                        px: 1.5,
                        cursor: 'pointer'
                    }}
                >
                    <Typography variant="body2" sx={{color: tokens.color.neutral950}}>
                        {email}
                    </Typography>
                    <Badge label="Verified" variant="success" />
                </Stack>
                {pendingEmail && <PendingEmailBanner pendingEmail={pendingEmail} onCancel={handleCancelPendingEmail} />}
            </FormCard>

            <ChangeEmailModal
                open={emailModalOpen}
                currentEmail={email}
                onClose={() => setEmailModalOpen(false)}
                onSave={handleSaveEmail}
                isLoading={isEmailLoading}
            />

            <AuthToast toast={toast} onClose={() => setToast(null)} />
        </Stack>
    );
};

export default ProfileSettingsPage;
